import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { Info } from "lucide-react";
import { dashboardTheme } from "../theme/dashboardTheme";

type MetricInfoTooltipVariant = "default" | "subtle";

interface MetricInfoTooltipProps {
  title: string;
  description: string;
  ariaLabel: string;
  variant?: MetricInfoTooltipVariant;
}

const VARIANT_STYLES: Record<
  MetricInfoTooltipVariant,
  {
    iconClassName: string;
    buttonClassName: string;
    titleClassName: string;
    descriptionClassName: string;
    contentGap: string;
    contentPadding: string;
    maxWidth: number;
    accentGradient: string;
  }
> = {
  default: {
    iconClassName: "h-5 w-5",
    buttonClassName:
      "text-[#6ea8ff] hover:text-[#8bbcff] focus-visible:outline-[#6ea8ff]",
    titleClassName: "text-[0.7rem] tracking-[0.06em]",
    descriptionClassName: "text-[0.78rem] leading-[1.55]",
    contentGap: "gap-2.5",
    contentPadding: "px-3.5 pb-3.5 pt-3",
    maxWidth: 300,
    accentGradient: `linear-gradient(90deg, ${dashboardTheme.colors.accent} 0%, ${dashboardTheme.colors.chartLine} 100%)`,
  },
  subtle: {
    iconClassName: "h-3.5 w-3.5",
    buttonClassName:
      "text-[#6e7681] hover:text-[#8b949e] focus-visible:outline-[#8b949e]",
    titleClassName: "text-[0.62rem] tracking-[0.05em]",
    descriptionClassName: "text-[0.7rem] leading-[1.5]",
    contentGap: "gap-2",
    contentPadding: "px-3 pb-3 pt-2.5",
    maxWidth: 260,
    accentGradient: `linear-gradient(90deg, ${dashboardTheme.colors.textMuted} 0%, ${dashboardTheme.colors.cardBorder} 100%)`,
  },
};

function TooltipContent({
  title,
  description,
  variant,
}: Pick<MetricInfoTooltipProps, "title" | "description" | "variant">) {
  const styles = VARIANT_STYLES[variant ?? "default"];

  return (
    <div className="relative overflow-hidden rounded-xl">
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{ background: styles.accentGradient }}
      />
      <div className={`flex flex-col ${styles.contentGap} ${styles.contentPadding}`}>
        <span
          className={`font-bold uppercase ${styles.titleClassName}`}
          style={{ color: dashboardTheme.colors.textPrimary }}
        >
          {title}
        </span>
        <p
          className={`m-0 ${styles.descriptionClassName}`}
          style={{ color: dashboardTheme.colors.textSecondary }}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

export default function MetricInfoTooltip({
  title,
  description,
  ariaLabel,
  variant = "default",
}: MetricInfoTooltipProps) {
  const styles = VARIANT_STYLES[variant];

  return (
    <Tooltip
      title={<TooltipContent title={title} description={description} variant={variant} />}
      arrow
      placement="top"
      enterTouchDelay={0}
      leaveTouchDelay={4000}
      slotProps={{
        tooltip: {
          sx: {
            p: 0,
            maxWidth: styles.maxWidth,
            backgroundColor: dashboardTheme.colors.surfaceElevated,
            border: `1px solid ${dashboardTheme.colors.cardBorder}`,
            borderRadius: variant === "subtle" ? "10px" : "12px",
            boxShadow: dashboardTheme.shadow.card,
            overflow: "hidden",
          },
        },
        arrow: {
          sx: {
            color: dashboardTheme.colors.surfaceElevated,
            "&::before": {
              border: `1px solid ${dashboardTheme.colors.cardBorder}`,
            },
          },
        },
      }}
    >
      <button
        type="button"
        className={`inline-flex shrink-0 cursor-help items-center justify-center rounded-full border-0 bg-transparent p-0 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${styles.buttonClassName}`}
        aria-label={ariaLabel}
      >
        <Info className={styles.iconClassName} strokeWidth={2} aria-hidden />
      </button>
    </Tooltip>
  );
}
